<script>
import SocialVisitsService from '@/services/SocialVisitsService';
import VisitLocationsService from '@/services/VisitLocationsService';

export default {
  name: 'SocialVisits',
  data() {
    return {
      loading: true,
      visits: [],
      fields: [
        { label: 'Requested On', key: 'requestedOn' },
        { label: 'Location', key: 'location' },
        { label: 'Visitor', key: 'visitor' },
        { label: 'Resident', key: 'resident' },
        { label: 'Relationship', key: 'relationship' },
        { label: 'Booked Slot', key: 'bookedSlot' },
        { label: 'Status', key: 'status' },
      ],
      filters: {
        status: null,
        visitLocationId: '',
        visitorFirstName: '',
        visitorLastName: '',
        prisonerNumber: '',
        before: null,
        after: null,
        sort: 'booked_slot.start_date_time',
      },
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      sortOptions: [
        { value: 'booked_slot.start_date_time', text: 'Booked Slot (ascending)' },
        { value: '-booked_slot.start_date_time', text: 'Booked Slot (descending)' },
        { value: 'created_at', text: 'Requested On (ascending)' },
        { value: '-created_at', text: 'Requested On (descending)' },
      ],
      statusOptions: [
        { value: null, text: 'All' },
        { value: 'Requested', text: 'Requested' },
        { value: 'AwaitingConfirmation', text: 'Awaiting Confirmation' },
        { value: 'Approved', text: 'Approved' },
        { value: 'Completed', text: 'Completed' },
        { value: 'Rejected', text: 'Rejected' },
        { value: 'Cancelled', text: 'Cancelled' },
      ],
      debounceTimer: null,
      visitLocations: [],
    };
  },
  computed: {
    pageTitle() {
      switch (this.filters.status) {
        case 'Requested':
          return 'Requested Social Visits';
        case 'AwaitingConfirmation':
          return 'Social Visits Awaiting Confirmation';
        case 'Approved':
          return 'Approved Social Visits';
        case 'Completed':
          return 'Completed Social Visits';
        case 'Rejected':
          return 'Rejected Social Visits';
        case 'Cancelled':
          return 'Cancelled Social Visits';
        default:
          return 'All Social Visits';
      }
    },
    visitLocationOptions() {
      const locations = this.visitLocations.map(location => ({
        value: location._id,
        text: location.name,
      }));
      locations.push({ value: '', text: 'All' })
      return locations;
    },
  },
  async mounted() {
    const { status } = this.$route.meta;
    this.filters.status = status || null;
    try {
      await Promise.all([
        this.getVisitLocations(),
        this.getVisits(),
      ])
      this.loading = false;
    } catch (e) {
      this.$router.go(-1);
    }
  },
  methods: {
    async getVisitLocations() {
      try {
        const response = await VisitLocationsService.getVisitLocationsForPrison();
        this.visitLocations = response.data;
      } catch (e) {
        console.error('Error getting visit locations: ', e);
        const res = e.response;
        let errorText = 'Error fetching visit locations, please try again.';
        if (res && res.data.error) {
          errorText = res.data.error;
        }
        this.$toast.error(errorText);
        throw e;
      }
    },
    async getVisits() {
      try {
        const response = await SocialVisitsService.getVisitsPaginated({
          ...this.filters,
          skip: (this.currentPage - 1) * this.perPage,
          limit: this.perPage,
        });
        this.visits = response.data.data;
        this.totalCount = response.data.count;
      } catch (e) {
        console.error('Error getting social visits: ', e);
        const res = e.response;
        let errorText = 'Error fetching social visits, please try again.';
        if (res && res.data.error) {
          errorText = res.data.error;
        }
        this.$toast.error(errorText);
        throw e
      }
    },
    search() {
      this.currentPage = 1;
      this.debounce(this.getVisits)();
    },
    updateFilter() {
      this.currentPage = 1;
      this.getVisits();
    },
    debounce(func, timeout = 300) {
      return (...args) => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    },
    clearFilters() {
      this.currentPage = 1;
      this.filters = {
        status: null,
        visitorFirstName: '',
        visitorLastName: '',
        prisonerNumber: '',
        before: null,
        after: null,
        sort: 'created_at',
      };
      this.getVisits();
    },
    pushDetailsPage(visit) {
      this.$store.dispatch('socialVisits/setVisit', visit);
      this.$router.push({ name: 'social-visit-details', params: { id: visit._id } });
    },
  },
};
</script>

<template>
  <div v-if="loading">
    <div class="col text-center mt-5">
      <span class="fas fa-spinner fa-spin" style="font-size: 2em;" />
    </div>
  </div>
  <div v-else>
    <b-card no-body class="mb-0">
      <b-row class="p-2 d-flex justify-content-between">
        <b-col>
          <h2>
            {{ pageTitle }}
          </h2>
        </b-col>
        <b-col>
          <b-btn
            v-if="filters.status === 'Approved'"
            class="float-right ml-1"
            variant="primary"
            :to="{ name: 'print-social-visits' }"
          >
            Print Visits
          </b-btn>
          <b-btn class="float-right" variant="outline-primary" :to="{ name: 'book-social-visit' }">
            Book Social Visit
          </b-btn>
        </b-col>
      </b-row>
      <b-col class="p-2">
        <b-row class="mb-2 d-flex justify-content-end">
          <!--Clear filters-->
          <b-col cols="3">
            <b-btn class="w-100" @click="clearFilters">
              Clear Filters
            </b-btn>
          </b-col>
        </b-row>
        <b-row class="mb-2 align-items-end">
          <!--Visitor Location-->
          <b-col cols="3">
            <label>
              Visit Location
            </label>
            <b-input-group>
              <b-form-select
                v-model="filters.visitLocationId"
                :options="visitLocationOptions"
                class="d-inline-block"
                placeholder="Select location"
                @change="updateFilter"
              />
            </b-input-group>
          </b-col>

          <!--Visitor first name-->
          <b-col cols="3">
            <label>
              Visitor Firstname
            </label>
            <b-input-group>
              <b-form-input
                v-model="filters.visitorFirstName"
                class="d-inline-block"
                placeholder="Search visitor firstname"
                @keydown.enter="search"
                @input="search"
              />
            </b-input-group>
          </b-col>

          <!--Visitor last name-->
          <b-col cols="3">
            <label>
              Visitor Lastname
            </label>
            <b-input-group>
              <b-form-input
                v-model="filters.visitorLastName"
                class="d-inline-block"
                placeholder="Search visitor lastname"
                @keydown.enter="search"
                @input="search"
              />
            </b-input-group>
          </b-col>

          <!--Prisoner number-->
          <b-col cols="3">
            <label>
              Resident ID
            </label>
            <b-input-group>
              <b-form-input
                v-model="filters.prisonerNumber"
                class="d-inline-block"
                placeholder="Search resident id"
                @keydown.enter="search"
                @input="search"
              />
            </b-input-group>
          </b-col>
        </b-row>
        <b-row>
          <!--After date-->
          <b-col>
            <label>Booked For (after) <feather-icon v-b-tooltip.hover title="Show only records booked on or after this date." icon="InfoIcon" size="14" /></label>
            <b-form-datepicker
              v-model="filters.after"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" close-button
              cy="before"

              reset-button
              today-button
              @input="updateFilter"
            />
          </b-col>

          <!--Before date-->
          <b-col>
            <label>Booked For (before) <feather-icon v-b-tooltip.hover title="Show only records booked before this date." icon="InfoIcon" size="14" /></label>
            <b-form-datepicker
              v-model="filters.before"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" close-button
              cy="before"

              reset-button
              today-button
              @input="updateFilter"
            />
          </b-col>

          <!--Sort-->
          <b-col>
            <label>Sort</label>
            <b-form-select
              v-model="filters.sort"
              :options="sortOptions"
              cy="sort"
              @change="updateFilter"
            />
          </b-col>

          <!--Status-->
          <b-col>
            <label>
              Status
            </label>
            <b-form-select
              v-model="filters.status"
              :options="statusOptions"
              cy="sort"
              @change="updateFilter"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-table
        :current-page="currentPage"
        :fields="fields"
        :items="visits"
        :responsive="true"
        class="data-table mb-0"
        hover
        show-empty
        @row-clicked="pushDetailsPage"
      >
        <template #cell(requestedOn)="data">
          {{ data.item.created_at | formatDateTime }}
        </template>
        <template #cell(location)="data">
          {{ data.item.visit_location.name }}
        </template>
        <template #cell(visitor)="data">
          {{ data.item.visitor.firstname }} {{ data.item.visitor.lastname }}
        </template>
        <template #cell(resident)="data">
          {{ data.item.prisoner.firstname }} {{ data.item.prisoner.lastname }} ({{
            data.item.prisoner.prisoner_number
          }})
        </template>
        <template #cell(relationship)="data">
          {{ data.item.prisoner.relationship_to_booker }}
        </template>
        <template #cell(bookedSlot)="data">
          {{ data.item.booked_slot.start_date_time | formatDateTime }} -
          {{ data.item.booked_slot.end_date_time | formatTime }}
        </template>
        <template #cell(status)="data">
          {{ data.item.status === 'AwaitingConfirmation' ? 'Awaiting Confirmation' : data.item.status }}
        </template>
      </b-table>

      <b-row v-if="visits && visits.length">
        <b-col class="m-1">
          <b-pagination
            v-model="currentPage"
            :per-page="perPage"
            :total-rows="totalCount"
            class="my-0"
            @input="getVisits"
          />
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>
